import DOMPurify from "dompurify";

/**
 * A static block (`display.kind === "static"`) renders in the host page
 * instead of an iframe: sanitised, its stylesheets scoped onto one wrapper,
 * inside a shadow root. The creator declares the kind; nothing here decides
 * it from the body.
 */

/** Nothing that runs: scripts, handler attributes and `javascript:` urls
 *  (DOMPurify drops those by default), frames, plugins and forms. `data-*`
 *  stays — islands are `data-edit-id`. */
export const STATIC_BLOCK_SANITIZE: Parameters<typeof DOMPurify.sanitize>[1] = {
  WHOLE_DOCUMENT: true,
  ADD_TAGS: ["style"],
  FORBID_TAGS: [
    "script",
    "iframe",
    "object",
    "embed",
    "link",
    "meta",
    "base",
    "form",
  ],
  FORBID_ATTR: [
    "onabort",
    "onblur",
    "oncancel",
    "oncanplay",
    "onchange",
    "onclick",
    "onclose",
    "oncontextmenu",
    "oncopy",
    "oncut",
    "ondblclick",
    "ondrag",
    "ondragend",
    "ondragenter",
    "ondragleave",
    "ondragover",
    "ondragstart",
    "ondrop",
    "onerror",
    "onfocus",
    "oninput",
    "oninvalid",
    "onkeydown",
    "onkeypress",
    "onkeyup",
    "onload",
    "onloadeddata",
    "onloadstart",
    "onmousedown",
    "onmouseenter",
    "onmouseleave",
    "onmousemove",
    "onmouseout",
    "onmouseover",
    "onmouseup",
    "onpaste",
    "onplay",
    "onpointercancel",
    "onpointerdown",
    "onpointerenter",
    "onpointerleave",
    "onpointermove",
    "onpointerout",
    "onpointerover",
    "onpointerup",
    "onreset",
    "onresize",
    "onscroll",
    "onselect",
    "onsubmit",
    "ontoggle",
    "ontouchend",
    "ontouchmove",
    "ontouchstart",
    "ontransitionend",
    "onunload",
    "onwheel",
  ],
  ALLOW_DATA_ATTR: true,
};

export interface PreparedStaticBlock {
  /** The block's own stylesheets, scoped onto `.block-root`. */
  css: string;
  /** The sanitised body markup, without its `<style>` elements. */
  html: string;
}

export function prepareStaticBlock(body: string): PreparedStaticBlock {
  const clean = DOMPurify.sanitize(
    body,
    STATIC_BLOCK_SANITIZE,
  ) as unknown as string;
  const doc = new DOMParser().parseFromString(clean, "text/html");
  const styles = [...doc.querySelectorAll("style")];
  const css = styles.map((s) => s.textContent ?? "").join("\n");
  for (const style of styles) style.remove();
  return { css: scopeBlockCss(css), html: doc.body.innerHTML };
}

/**
 * Rewrite a block stylesheet's document-level selectors onto the shadow
 * root's single wrapper: `html`, `body` and `:root` all become
 * `.block-root` (`:host` for the token sheet, so the custom properties land
 * where the subtree inherits them).
 *
 * A regex over rule heads (the text between a brace and the next `{`), not
 * a CSS parser; at-rule preludes are left alone, so `@media` and
 * `@supports` conditions survive intact.
 */
export function scopeBlockCss(
  css: string,
  { rootSelector = ".block-root" }: { rootSelector?: string } = {},
): string {
  return css.replace(/([^{}]*?)\{/g, (m: string, head: string) =>
    head.includes("@") ? m : `${rewriteSelectorList(head, rootSelector)}{`,
  );
}

function rewriteSelectorList(head: string, rootSelector: string): string {
  return head
    .replace(/:root\b/g, rootSelector)
    .replace(
      /(^|[\s,>+~(])(html|body)\b/gi,
      (_m, lead: string) => `${lead}${rootSelector}`,
    );
}
